/**
 * Simplified Chinese message catalog.
 *
 * Typed as Record<MessageKey, string>, so it defines every key in en.ts.
 */
import type { MessageKey } from './en';

export const zh: Record<MessageKey, string> = {
  // Common
  'common.unknown': '未知',
  'common.tryAgain': '重试',
  'common.walletFallback': '钱包',
  'common.loadingWallets': '正在加载钱包...',
  'common.back': '返回',
  'common.close': '关闭',
  'common.loading': '正在加载…',
  'common.select': '选择',
  'common.selected': '已选择',
  'common.beta': 'BETA',
  'common.cancel': '取消',
  'common.refresh': '刷新',
  'common.refreshing': '正在刷新',
  'common.all': '全部',
  'common.wallets': '个钱包',
  'common.wallet': '个钱包',
  'common.networks': '个网络',
  'common.network': '个网络',
  'common.pending': '待处理',
  'common.unavailable': '不可用',
  'common.unverified': '未验证',
  'common.settings': '设置',
  'menu.title': '菜单',
  'menu.open': '打开菜单',
  'menu.close': '关闭菜单',
  'common.tier.testnet': '测试网',
  'common.tier.mainnet': '主网',
  'common.theme.auto': '自动',
  'common.theme.light': '浅色',
  'common.theme.dark': '深色',

  // Navigation
  'nav.primary': '主导航',
  'nav.home': '首页',

  // Settings — page
  'settings.title': '设置',

  // Settings — approvals section
  'settings.approvals.title': '审批',
  'settings.approvals.hint': '在网关签名之前，查看等待您决定的交易。',
  'settings.approvals.link': '查看待审批交易',

  // Settings — appearance section
  'settings.appearance.title': '外观',
  'settings.appearance.hint': '选择应用的显示方式。',
  'settings.appearance.themeGroup': '主题',

  // Settings — language section
  'preauth.menu.title': '语言与外观',
  'preauth.menu.open': '打开语言与外观菜单',
  'settings.language.title': '语言',
  'settings.language.hint': '选择您的语言。',

  // Settings — developer mode section
  'settings.devmode.title': '开发者模式',
  'settings.devmode.label': '开发者模式',
  'settings.devmode.desc': '开启时使用测试网，关闭时使用主网。',
  'settings.devmode.note': '当前为测试网模式，余额没有实际价值。',

  // Settings — deployment section
  'settings.deployment.title': '部署',
  'settings.deployment.network': '网络',
  'settings.deployment.gatewayEnv': '网关环境',
  'settings.deployment.bitcoinNetwork': 'Bitcoin 网络',
  'settings.deployment.organization': '组织',

  // Settings — security section (device passkeys)
  'settings.security.title': '安全',
  'settings.security.hint': '添加设备通行密钥，以便网关签名前在此设备上批准交易。',
  'settings.security.methodsTitle': '验证方式',
  'settings.security.addDevice': '添加验证方式',
  'settings.security.limitReached': '最多可添加 {max} 种验证方式。',
  'settings.security.adding': '正在等待设备…',
  'settings.security.remove': '移除',
  'settings.security.removing': '正在移除…',
  'settings.security.empty': '尚未添加设备通行密钥。',
  'settings.security.loading': '正在加载验证方式…',
  'settings.security.retry': '重试',
  'settings.security.unsupported': '此浏览器不支持设备通行密钥。',
  'settings.security.enrolledOn': '添加于 {date}',
  'settings.security.lastUsed': '上次使用于 {date}',
  'settings.security.deviceFallbackLabel': '设备通行密钥',

  // Create-a-passkey prompt (one-time, after a fresh OAuth or OTP login)
  'passkey.create.title': '创建通行密钥',
  'passkey.create.body': '使用指纹、面容或屏幕锁登录，无需记住密码。',
  'passkey.create.cta': '创建通行密钥',
  'passkey.create.creating': '正在等待您的设备…',
  'passkey.create.dismiss': '不再显示',
  'passkey.create.error': '无法创建通行密钥。请重试，或稍后在菜单的“安全”中添加。',
  'passkey.stateUnknown': '转账未发送。因网络连接问题无法确认您的验证方式，请重试。',

  // Settings — account section
  'settings.account.title': '账户',
  'settings.account.signout': '退出登录',

  // Login — page
  'login.title': '继续前往您的钱包',
  'login.eyebrow': '欢迎',
  'login.error.start': '无法开始登录。请检查网络连接后重试。',
  'login.divider': '或',
  'login.more': '更多登录方式',

  // Login — providers ({name} is the brand for the fallback label)
  'login.provider.google': '使用 Google 继续',
  'login.provider.apple': '使用 Apple 继续',
  'login.provider.discord': '使用 Discord 继续',
  'login.provider.twitter': '使用 X 继续',
  'login.provider.kakao': '使用 Kakao 继续',
  'login.provider.fallback': '使用 {name} 继续',
  'login.provider.unavailable': '此登录方式目前不可用。',

  // Login — passkey sign-in
  'login.passkey.button': '安全登录',
  'login.passkey.desc': '使用人脸识别、指纹识别等您设备上已注册的验证方式。',
  'login.passkey.pending': '正在等待通行密钥…',
  'login.passkey.cancelled':
    '登录已取消，或者此设备上没有该网站的通行密钥。请重试或选择其他登录方式。',
  'login.passkey.error': '无法使用通行密钥登录。请重试或选择其他登录方式。',
  // Shown when sign-in found no passkey or no account. Passkey creation lives in the
  // low-weight "create an account with a passkey" link below, so this points there.
  'login.passkey.noAccount': '此设备上未找到通行密钥。请使用下方的“创建钱包”。',

  // Login — passkey signup (a low-weight link below sign-in for a new user)
  'login.signup.prompt': '初次使用？',
  'login.signup.link': '创建钱包',
  'login.signup.pending': '正在创建账户…',
  'login.signup.exists': '您已有账户，请改用通行密钥登录。',
  'login.signup.error': '无法创建账户。请重试或选择其他登录方式。',

  // Login — organization escape hatch (inside the "more ways to sign in" section)
  'login.org.label': '组织 ID',
  'login.org.note': '留空即可登录个人空间，然后在上方选择登录方式。',

  // Login — developer fallback
  'login.dev.body': '在本地开发环境连接网关时，请从浏览器控制台设置令牌：',

  // Dashboard (home)
  'dashboard.offline': '您似乎已离线。请重新连接以加载钱包。',
  'dashboard.empty': '还没有钱包。创建一个即可开始。',

  // Home components
  'home.account.allWallets': '所有钱包',
  'home.account.subtitle': '{wallets} {walletWord} · {networks} {networkWord}',
  'home.switcher.aria': '切换账户，当前为 {name}',
  'home.copy.aria': '复制 {name} 地址',
  'home.portfolio.aria': '投资组合余额',
  'home.portfolio.total': '总余额',
  'home.actions.aria': '钱包操作',
  'home.action.swapUnavailable': '兑换功能暂不可用。',
  'home.action.buySellUnavailable': '买卖功能暂不可用。',
  'home.action.buySell': '买入/卖出',
  'home.network.testnetTitle': '测试网部署。此处显示的余额没有实际价值。',
  'home.network.mainnetTitle': '主网。余额反映实时网络数据。',

  // App navigation / account-wide surfaces
  'activity.title': '活动',
  'activity.history': '历史记录',
  'activity.detail.aria': '交易详情',
  'activity.detail.asset': '资产',
  'activity.detail.wallet': '发送钱包',
  'activity.detail.confirmations': '确认数',
  'activity.detail.time': '完成时间',

  // Transaction list
  'tx.aria.history': '交易历史记录',
  'tx.date.pending': '待处理',
  'tx.direction.receive': '接收',
  'tx.direction.send': '发送',
  'tx.direction.swap': '兑换',
  'tx.action.sent': '已发送',
  'tx.action.received': '已接收',
  'tx.action.swapped': '已兑换',
  'tx.action.approval': '代币授权',
  'tx.action.generic': '交易',
  'tx.group.today': '今天',
  'tx.group.thisWeek': '本周',
  'tx.time.todayAt': '今天 {time}',
  'tx.time.lastWeekdayAt': '上{weekday} {time}',
  'tx.sub.received': '已接收 {asset}',
  'tx.sub.sent': '已发送 {asset}',
  'tx.row.viewOnExplorer': '在浏览器中查看',
  'tx.relation.from': '来自',
  'tx.relation.to': '发送至',
  'tx.swapDetails.aria': '兑换详情：从 {from} 到 {to}',
  'tx.status.pending': '待处理',
  'tx.status.pending_approval': '等待批准',
  'tx.status.pending_policy': '策略检查',
  'tx.status.signing': '正在签名',
  'tx.status.signed': '已签名',
  'tx.status.submitted': '已提交',
  'tx.status.broadcast': '已广播',
  'tx.status.confirmed': '已确认',
  'tx.status.failed': '失败',
  'tx.status.rejected': '已拒绝',
  'tx.status.unknown': '未知',
  'tx.loading': '正在加载活动…',
  'tx.empty': '暂无活动。',
  'tx.empty.hint': '发送和兑换记录会显示在这里。',
  'tx.loadingMore': '正在加载更多…',
  'tx.explorer.aria': '在区块浏览器中查看',

  // Approvals
  'approvals.title': '审批',
  'approvals.error.load': '无法加载审批',
  'approvals.empty.title': '全部处理完毕',
  'approvals.empty.body': '没有交易等待决定。需要您查看的新请求会显示在这里。',
  'approvals.wallet.title': '此钱包的审批',
  'approvals.wallet.resolving': '正在检查哪些审批属于此钱包…',
  'approvals.wallet.empty.title': '此钱包没有待处理项',
  'approvals.wallet.empty.body': '此钱包没有交易等待决定。需要您查看的请求会显示在这里。',
  'approvals.status.pending': '待处理',
  'approvals.status.approved': '已批准',
  'approvals.status.rejected': '已拒绝',
  'approvals.status.expired': '已过期',
  'approvals.quorum': '已获 {received}/{required} 个批准',
  'approvals.quorum.aria': '法定人数进度',
  'approvals.waiting.more': '还需等待 {count} 个{word}',
  'approvals.waiting.approval': '批准',
  'approvals.waiting.approvals': '批准',
  'approvals.waiting.signing': '正在等待签名流程完成',
  'approvals.timeline.checker': '检查者',
  'approvals.timeline.acted': '已操作',
  'approvals.detail.loading': '正在加载确切交易…',
  'approvals.detail.unavailable': '无法加载交易详情。仅当您能通过其他方式确认此转账时才批准或拒绝。',
  'approvals.detail.notProvided': '交易记录未提供',
  'approvals.detail.requestedBy': '请求者',
  'approvals.detail.transactionStatus': '交易状态',
  'approvals.summary.transaction': '交易',
  'approvals.action.busy': '正在协调多方签名。达到法定人数后，网关会先签名并广播再响应，这可能需要几秒钟。',
  'approvals.action.approve': '批准',
  'approvals.action.reject': '拒绝',
  'approvals.confirm.approveTitle': '批准此交易？',
  'approvals.confirm.rejectTitle': '拒绝此交易？',
  'approvals.confirm.approveConsequence':
    '您的批准将发送至网关。达到法定人数后，网关会先签名并广播此交易再响应，这可能需要几秒钟。完成前请勿关闭应用。',
  'approvals.confirm.rejectConsequence': '这会阻止交易签名。法定人数将无法达到，因此交易不会广播。',
  'approvals.confirm.reasonLabel': '原因（可选）',
  'approvals.confirm.reasonPlaceholder': '添加审计记录备注',
  'approvals.confirm.approving': '正在批准',
  'approvals.confirm.rejecting': '正在拒绝',
  'approvals.section.pending': '需要您查看（{count}）',
  'approvals.section.decided': '已决定',

  // Create wallet
  'create.title': '创建钱包',
  'create.lead': '选择钱包类型和名称。密钥生成在服务器上安全运行；您的设备不存储密钥，也不会在此签名。',
  'create.walletName': '钱包名称',
  'create.walletPlaceholder': '我的钱包',
  'create.walletType': '钱包类型',
  'create.kind.evm.label': 'EVM 账户',
  'create.kind.evm.desc': 'Ethereum · Base · BSC · Hyperliquid',
  'create.kind.tron.label': 'Tron',
  'create.kind.tron.desc': 'TRX 与 TRC-20 代币',
  'create.kind.solana.label': 'Solana',
  'create.kind.solana.desc': 'SOL 与 SPL 代币',
  'create.kind.solanaTestnet.label': 'Solana 测试网',
  'create.kind.solanaTestnet.desc': '测试网上的 SOL 与 SPL 代币',
  'create.kind.solanaDevnet.label': 'Solana 开发网',
  'create.kind.solanaDevnet.desc': '开发网上的 SOL 与 SPL 代币',
  'create.kind.sui.label': 'Sui',
  'create.kind.sui.desc': 'SUI 与 Sui 代币',
  'create.kind.suiTestnet.label': 'Sui 测试网',
  'create.kind.suiTestnet.desc': '测试网上的 SUI 与 Sui 代币',
  'create.kind.suiDevnet.label': 'Sui 开发网',
  'create.kind.suiDevnet.desc': '开发网上的 SUI 与 Sui 代币',
  'create.kind.bitcoin.label': 'Bitcoin',
  'create.kind.bitcoin.desc': 'BTC · SegWit 地址',

  'provisioning.title': '正在创建您的钱包',
  'provisioning.unavailable': '此处不可用',
  'walletSettings.statusFrozen': '冻结',
  'dashboard.stillCreating': '还有 {count} 个钱包正在创建中。',
  'dashboard.createWallet': '创建钱包',
  'provisioning.unconfirmed': '尚未确认',
  'provisioning.creationFailed': '创建失败',
  'provisioning.failed': '无法自动创建您的钱包。',
  'provisioning.createManually': '手动创建钱包',
  'provisioning.succeeded': '您的钱包已准备就绪。',
  'provisioning.partialFailure': '无法创建 {missing} 钱包。',
  'provisioning.partialHint': '{missing} 钱包在此处不可用。',
  'provisioning.partialPending': '尚未确认 {pending} 钱包是否已创建。',
  'provisioning.partialCheckHint': '您可以尝试手动创建尚未创建的钱包。',
  'provisioning.pending': '钱包创建等待超时。',
  'provisioning.pendingHint': '您可以尝试手动创建尚未创建的钱包。',
  'provisioning.goHome': '继续',
  'create.busy': '正在执行密钥生成流程',
  'create.toast.created': '钱包已创建。',

  // OAuth callback
  'oauth.pending.title': '正在登录',
  'oauth.pending.message': '正在完成登录，请稍候。',
  'oauth.failure.title': '登录未完成',
  'oauth.failure.fallback': '无法完成登录。',
  'oauth.backToSignIn': '返回登录',

  // Receive ({chain} is the resolved chain name)
  'receive.title': '接收',
  'receive.subtitle': '选择接收资产的网络和地址',
  'receive.chain.fallback': '此钱包',
  'receive.chainSelector.aria': '选择接收资产的网络',
  'receive.addressPicker.label': '地址',
  'receive.addressPending': '地址待生成',
  'receive.qr.title': '{chain} 地址的二维码',
  'receive.qr.unavailable': '二维码不可用。请复制下方地址。',
  'receive.addressLabel': '{chain} 地址',
  'receive.warning': '仅向此地址发送 {chain} 资产。',
  'receive.copy.copy': '复制地址',
  'receive.copy.copied': '地址已复制',
  'receive.copy.failed': '复制失败',
  'receive.copy.aria': '将地址复制到剪贴板',
  'receive.pending.setup': '此钱包仍在设置中。准备就绪后会显示地址。',
  'receive.pending.loading': '正在加载此钱包的地址...',
  'receive.empty': '尚无可接收资产的钱包。',

  // Send ({asset}, {chain}, {label}, {reason} are interpolated at render)
  'send.title': '发送',
  'send.selectAsset': '选择要发送的资产',
  'send.loadingAssets': '正在加载资产...',
  'send.emptyAssets': '暂无可用余额的资产。请先接收资产再发送。',
  'send.emptyNoWallets': '还没有钱包。请先创建钱包再发送。',
  'send.backToAssets': '返回资产列表',
  'send.stepTwo.title': '在 {chain} 网络发送 {asset}',
  'send.context.none': '未选择钱包。',
  'send.context.from': '从 {chain} 上的 {label} 发送',
  'send.context.fromUnsupported': '从 {label} 发送。{reason}',
  'send.error.wrongTier': '此钱包不在当前网络上。请切换网络，或选择当前网络上的钱包后再发送。',
  'send.error.noWallet': '选择用于发送的钱包。',
  'send.error.chainUnavailable': '此网络暂不支持发送。',
  'send.error.tokenSendDisabled': '此网络暂不支持发送代币。',
  'send.status.submitting': '正在提交交易。',
  'send.status.buildingPayload': '正在读取网络费用和 nonce。',
  'send.status.pendingPolicy': '此转账需要额外批准，或受策略时间锁限制。满足条件前不会继续。',
  'send.status.noApproval': '交易已创建，但无法在此处理其审批。请前往审批页面查看并继续。',
  'send.status.ambiguous': '此转账可能已经发送，但我们无法确认。再次尝试前请先查看活动记录。',
  'send.status.creationUnknown': '交易可能已创建，但我们无法确认。再次尝试前请先查看活动记录。',
  'send.status.approvalPending': '交易已创建但尚未批准。请前往审批页面继续。',
  'send.confirm.title': '确认发送',
  'send.confirm.note': '请仔细核对目标地址。资金转出后无法撤销。',
  'send.confirm.to': '接收地址',
  'send.confirm.amount': '金额',
  'send.confirm.network': '网络',
  'send.confirm.networkFee': '网络费用',
  'send.confirm.networkFeeValue': '网关构建交易时会估算并应用费用。',
  'send.confirm.networkFeeValueEvm': '在您确认时从网络读取，并应用于该交易。',
  'send.confirm.feeSplRent': '收款方可能需要创建代币账户（约 0.00204 {native} 租金，由你支付）。',
  'send.confirm.feeSuiGas': '需要另一枚 {native} 代币来支付 Gas。',
  'send.confirm.submit': '确认并发送',
  'send.confirm.sending': '正在发送',
  'send.status.title': '发送状态',
  'send.status.statusLabel': '状态',
  'send.status.txId': '交易 ID',
  'send.status.hash': '哈希',
  'send.status.explorer': '在区块浏览器中查看',
  'send.status.goToApprovals': '前往审批',
  'send.status.goToActivity': '前往活动记录',
  'send.status.backToSend': '返回发送',
  'send.result.status': '转账{status}。',
  'send.result.statusReason': '转账{status}{reason}',

  // Send form and asset picker
  'send.form.recipient': '接收地址',
  'send.form.destinationPlaceholder': '目标地址',
  'send.form.noWallet': '选择用于发送的钱包。',
  'send.form.chainUnavailable': '此网络暂不支持在这里发送。',
  'send.validation.recipientRequired': '请输入接收地址。',
  'send.validation.evmWebUnavailable': '网页应用暂不支持在此网络发送。',
  'send.validation.evmNoNetwork': '请选择要发送的币种以确定网络。此账户在所有 EVM 网络上使用同一个地址。',
  'send.validation.evmTokenUnsupported': '网页应用暂不支持在 EVM 网络上发送代币。请使用移动应用转账代币，或在此发送该网络的原生币。',
  'send.validation.tokenNotOnWeb': '网页应用暂不支持在此网络上发送代币。此处只能发送该网络的原生币。',
  'send.validation.evmImportedTokenUnsupported': '导入的代币无法从网页应用发送。此处仅可发送注册表已验证的代币和网络自身的币。',
  'send.validation.evmTokenDecimalsUnknown': '无法确定此代币的精度，因此无法安全地编码此转账。未签名任何内容。',
  'send.validation.evmPrefix': 'EVM 地址以 0x 开头。',
  'send.validation.evmFormat': 'EVM 地址是 0x 后跟 40 个十六进制字符。',
  'send.validation.evmChecksum': '该 EVM 地址的校验和无效。',
  'send.validation.evmZeroAddress': '这是零地址，发送到该地址的资产无法找回。',
  'send.validation.suiPrefix': 'Sui 地址以 0x 开头。',
  'send.validation.suiFormat': 'Sui 地址由 0x 和其后的 64 个小写十六进制字符组成。',
  'send.validation.tronPrefix': 'Tron 地址以 T 开头。',
  'send.validation.tronLength': 'Tron 地址为 34 个字符。',
  'send.validation.tronChecksum': '此 Tron 地址的校验和无效。',
  'send.validation.tronInvalid': '这不是有效的 Tron 地址。',
  'send.validation.solanaInvalid': '这似乎不是有效的 Solana 地址。',
  'send.validation.solanaBytes': 'Solana 地址解码后应为 32 字节。',
  'send.validation.bitcoinChecksum': '此 Bitcoin 地址的校验和无效。',
  'send.validation.bitcoinInvalid': '这不是有效的 Bitcoin 地址。',
  'send.validation.bitcoinSegwitInvalid': '这不是有效的 SegWit 地址。',
  'send.validation.bitcoinTaprootInvalid': '这不是有效的 Taproot 地址。',
  'send.validation.bitcoinWrongNetwork': '此地址属于其他 Bitcoin 网络（应为 {expected}）。',
  'send.validation.bitcoinDust': '金额低于 Bitcoin 的粉尘限额 {sats} sats。',
  'send.form.amountRequired': '请输入金额。',
  'send.form.numberRequired': '请输入数字。',
  'send.form.amountPositive': '金额必须大于零。',
  'send.form.decimals': '{symbol} 最多支持 {decimals} 位小数。',
  'send.form.exceedsBalance': '金额超过您的 {symbol} 余额。',
  'send.form.assetAria': '资产',
  'send.form.noAssets': '无资产',
  'send.form.available': '可用：{amount} {symbol}',
  'send.form.review': '查看发送详情',
  'send.form.clear': '清除',
  'send.form.max': '最大',
  'send.form.gasNeeded': '你需要 {native} 来支付网络费用。发送 {token} 前，请向此钱包添加 {native}。',
  'send.form.tokenDisabled': '此网络暂不支持发送代币。',
  'send.assets.searchLabel': '搜索资产',
  'send.assets.searchPlaceholder': '按名称或符号搜索',
  'send.assets.empty': '没有匹配的资产。清除搜索或筛选条件即可查看持仓。',

  // Swap ({decimals}, {provider}, {count} are interpolated at render)
  'swap.title': '兑换',
  'swap.subtitle': '比较所有提供商，选择最佳路径',
  'swap.balance': '余额 {amount} {symbol}',
  'swap.balance.checking': '正在检查可用余额…',
  'swap.balance.unavailable': '无法获取余额。',
  'swap.max': '最大',
  'swap.retry': '重试',
  'swap.amount.maxAfterFee': '扣除网络费后最多可用：{amount} {symbol}',
  'swap.confirm.selectToken': '选择代币',
  'swap.confirm.enterAmount': '输入金额',
  'swap.confirm.checkingBalance': '正在检查余额…',
  'swap.confirm.refreshBalance': '刷新余额',
  'swap.confirm.reduceAmount': '减少金额',
  'swap.confirm.findingRoutes': '正在查找路线…',
  'swap.confirm.refreshQuote': '正在刷新报价…',
  'swap.confirm.reviewVia': '查看 {provider} 兑换',
  'swap.confirm.findRoute': '查找路线',
  'swap.confirm.acceptImpact': '查看价格影响',
  'swap.confirm.highImpact': '我了解此路线的价格影响高达 {impact}%。',
  'swap.quote.bestReturn': '最高回报',
  'swap.quote.routes': '可用路线',
  'swap.quote.expires': '{seconds}秒后刷新',
  'swap.quote.refresh': '刷新报价',
  'swap.signer.overridden': '{selected} 不支持兑换，将改用 {label}（{address}）。',
  'swap.empty.evm': '请选择 EVM、Solana 或 Sui 钱包查看兑换报价。',
  'swap.error.loadChains': '无法加载网络',
  'swap.error.quote': '获取报价失败',
  'swap.error.executeNotEnabled': '此服务未启用兑换执行功能（开发签名器已关闭）。',
  'swap.error.executeFallback': '兑换失败',
  'swap.side.from': '你支付',
  'swap.side.to': '你获得',
  'swap.chain.fromAria': '选择付款钱包',
  'swap.chain.toAria': '选择收款钱包',
  'swap.amount.fromAria': '发送金额',
  'swap.amount.toAria': '接收金额',
  'swap.amount.invalidHint': '请检查金额后重试。',
  'swap.amount.zeroHint': '请输入大于 0 的金额。',
  'swap.amount.precisionHint': '小数位数不能超过 {decimals} 位。',
  'swap.flip.aria': '切换兑换方向',
  'swap.sameTokenHint': '请选择一个不同的接收代币。',
  'swap.warning.insufficientBalance': '您的余额低于此金额，兑换将会失败。',
  'swap.warning.insufficientAllowance': '此次兑换前需要先授权代币。',
  'swap.toast.submitted': '已通过 {provider} 提交兑换。可在活动中跟踪进度。',
  'swap.confirm.swapping': '正在兑换…',
  'swap.confirm.via': '通过 {provider} 兑换',
  'swap.confirm.summary': '将通过 {provider} 把 {inAmount} {inSymbol} 兑换为约 {outAmount} {outSymbol}。到账可能需要片刻。',
  'swap.mpcNote': '您的钱包会安全地签署此兑换；网络确认后资金即会到账。',
  'swap.confirm.default': '兑换',
  'swap.confirm.disabledTitle': '兑换执行功能将随网关签名集成提供',
  'swap.mock.enabledHint': '由开发钱包（模拟 MPC）签名 · 真实链上兑换',
  'swap.mock.disabledHint': '实时报价 · 兑换执行功能将随网关签名流程提供',
  'swap.picker.tokenAria': '选择代币',
  'swap.picker.chainAria': '选择网络',
  'swap.picker.searchToken': '搜索名称、符号或地址',
  'swap.picker.searchChain': '搜索钱包',
  'swap.picker.loadingTokens': '正在加载代币…',
  'swap.picker.noTokens': '没有匹配的代币。',
  'swap.picker.noChains': '没有匹配的网络。',
  'swap.picker.noWallets': '您的钱包都无法在可用网络上接收。',
  'swap.picker.routes': '{count} 条路径',
  'swap.token.aria': '选择代币',
  'swap.quote.noRoute': '无可用路径',
  'swap.quote.best': '最佳',
  'swap.quote.networkCost': '网络费用',
  'swap.quote.platformFee': '平台费用',
  'swap.quote.priceImpact': '价格影响',
  'swap.quote.time': '时间',

  // Swap detail sheet
  'swap.detail.aria': '兑换详情',
  'swap.detail.closeAria': '关闭详情',
  'swap.detail.status': '状态',
  'swap.detail.paid': '您支付了',
  'swap.detail.received': '您收到了',
  'swap.detail.rate': '汇率',
  'swap.detail.route': '路径',
  'swap.detail.provider': '提供商',
  'swap.detail.feeQuoted': '费用（报价）',
  'swap.detail.providerFee': '服务商费用',
  'swap.detail.networkFee': '网络费用',
  'swap.detail.feeCharged': '费用（实收）',
  'swap.detail.sourceTx': '源交易',
  'swap.detail.destinationTx': '目标交易',
  'swap.origin.unreported': '正在提交，结果尚不确定。',
  'swap.origin.reported': '正在网络上查询您的交易。',
  'swap.origin.verified': '源链上的交易已确认，正在等待目标链处理。',
  'swap.origin.reverted': '交易已在链上执行，但失败了。资金未发生转移。',
  'swap.origin.unknown': '无法确认这笔交易。再次尝试前，请先查看您的钱包记录。',
  'swap.origin.noRetry': '在确认这笔交易或确定其未发生之前，无法重试。',
  'swap.origin.ackPrompt': '此次兑换尚未确认。解除之前，请先查看您的钱包记录。',
  'swap.origin.ackRelease': '我已查看钱包记录 — 解除此保留',
  'swap.origin.trackingFailed':
    '由于无法记录此次兑换，因此未启动。未进行任何签名，资金也未发生转移。',

  // Earn (lending market; {symbol}, {amount}, {usd}, {digest}, {reason} interpolated)
  'earn.title': '借贷',
  'earn.subtitle': '将现有资产作为抵押借入资金，或出借资产赚取收益，提升资金利用率。',
  'earn.error.market': '市场不可用：{message}',
  'earn.error.retry': '重试',
  'earn.loading': '正在加载借贷市场…',
  'earn.empty': '目前没有可用的借贷市场。',
  'earn.table.asset': '资产',
  'earn.table.totalSupply': '总存款',
  'earn.table.totalBorrow': '总借款',
  'earn.table.ltv': 'LTV',
  'earn.table.aria': '借贷市场',
  'earn.vault.title': '{symbol} 金库',
  'earn.vault.supplyYield': '存款收益率',
  'earn.vault.borrowInterest': '借款利率',
  'earn.vault.marketPrice': '市场价格',
  'earn.vault.utilization': '利用率',
  'earn.vault.depositThreshold': '存款阈值',
  'earn.vault.borrowThreshold': '借款阈值',
  'earn.vault.borrowFee': '借款费用：{pct}%',
  'earn.account.title': '您的账户',
  'earn.account.deposits': '您的存款',
  'earn.account.borrows': '您的借款',
  'earn.account.borrowLimit': '借款限额',
  'earn.account.liqThreshold': '清算阈值',
  'earn.account.health': '账户健康度',
  'earn.account.depositedRow': '已存入 {amount} {symbol}（${usd}）',
  'earn.account.borrowedRow': '已借入 {amount} {symbol}（${usd}）',
  'earn.action.supply': '存入',
  'earn.action.borrow': '借入',
  'earn.action.withdraw': '提取',
  'earn.action.repay': '偿还',
  'earn.actions.aria': '借贷操作',
  'earn.amount.label': '金额（{symbol}）',
  'earn.amount.available': '可用约 {amount}',
  'earn.amount.max': '最大',
  'earn.amount.allDebt': '全部债务',
  'earn.submit.submitting': '正在提交…',
  'earn.note.sameAssetBorrow': '{symbol} 是您的抵押资产，无法借入。请选择其他资产，或先提取存款。',
  'earn.note.overMax.withdraw': '最多只能提取约 {amount} {symbol}，其余必须保留为未偿借款的抵押品。如需全部提取，请先还款。',
  'earn.note.overMax.borrow': '根据您的抵押品，最多可借入约 {amount} {symbol}。',
  'earn.note.overMax.repay': '您只需偿还约 {amount} {symbol}。',
  'earn.note.noSigner': '无法执行：签名通过网关完成，但网关借贷路径和本地开发签名器均无法连接。',
  'earn.note.devSigner': '开发签名器（测试网）：{address}',
  'earn.toast.confirmed': '{action}已确认（{digest}…）',
  'earn.toast.failedReason': '{action}失败：{reason}',
  'earn.toast.failedOnChain': '{action}失败：链上错误（{digest}…）',
  'earn.activity.title': '近期活动',

  // Filters and lists
  'filter.network.aria': '网络筛选器',
  'filter.chain.aria': '区块链筛选器',
  'filter.chain.selectNetwork': '选择网络',
  'holdings.empty': '这里还没有持仓。接收资产或切换筛选条件以查看更多。',
  'holdings.showHidden': '显示 {count} 个隐藏项 · 低于 $0.01',
  'holdings.hideSmall': '隐藏 {count} 个小额{word}',
  'holdings.balance': '余额',
  'holdings.balances': '余额',
  'chainSummary.empty': '这里还没有持仓。接收资产后即可查看网络。',
  'chainSummary.viewAria': '查看 {name}，{count} 个{word}',
  'walletChain.empty': '此网络上还没有钱包。',
  'walletChain.openAria': '打开 {label}',

  // Account and wallet management sheets
  'account.closeSwitcher': '关闭钱包切换器',
  'account.title': '您的钱包',
  'account.allDesc': '所有钱包，一览无余',
  'account.newWallet': '新建钱包',
  'account.searchPlaceholder': '按名称或地址搜索',
  'account.searchAria': '按名称或地址搜索钱包',
  'account.networks': '网络',
  'account.empty.filtered': '没有符合筛选条件的钱包。',
  'account.empty.none': '还没有钱包。',
  'account.selectAria': '选择 {label}，{value}',
  'account.copyAddressAria': '复制 {chain} 地址',
  'account.showQrAria': '显示 {chain} 接收二维码',
  'home.preference.filter': '代币网络',
  'home.preference.loading': '正在加载偏好设置…',
  'home.preference.unavailable': '无法获取已保存的网络',
  'home.preference.saving': '正在保存…',
  'home.preference.loadError': '无法加载已保存的网络。您仍可选择网络。',
  'home.preference.saveError': '无法确认此选择是否已保存。',
  'home.preference.testnet': '此筛选器适用于主网。测试网显示所有网络。',
  'selectNetwork.closeAria': '关闭网络选择器',
  'selectNetwork.title': '选择网络',
  'selectNetwork.searchPlaceholder': '搜索网络',
  'selectNetwork.searchAria': '按名称搜索网络',
  'selectNetwork.groupAria': '网络',
  'selectNetwork.all': '所有网络',
  'selectNetwork.empty': '没有匹配搜索条件的网络。',
  'walletTabs.sectionsAria': '钱包分区',
  'walletTabs.tokens': '代币',
  'walletTabs.nfts': 'NFT',
  'walletTabs.approvals': '审批',
  'walletTabs.manage': '管理加密资产',
  'walletTabs.history': '历史记录',
  'manage.closeAria': '关闭加密资产管理',
  'manage.aria': '管理 {wallet} 的加密资产',
  'manage.title': '管理加密资产',
  'manage.searchPlaceholder': '搜索代币',
  'manage.searchAria': '按符号或名称搜索代币',
  'manage.empty': '此钱包还没有代币。接收资产后即可在这里管理。',
  'manage.noMatches': '没有匹配搜索条件的代币。',
  'manage.tokensAria': '代币',
  'manage.showTokenAria': '在 {network} 上显示 {symbol}',
  'manage.import': '导入自定义代币',
  'manage.note': '仅列出此钱包持有的代币。可通过合约地址导入代币以跟踪自定义资产。导入的代币未经验证，也没有价格数据。',
  'import.closeAria': '关闭代币导入',
  'import.aria': '将代币导入 {wallet}',
  'import.title': '导入代币',
  'import.contract': '合约地址',
  'import.contractPlaceholder': '粘贴代币合约地址',
  'import.contractAria': '代币合约地址',
  'import.resolve': '解析代币',
  'import.resolving': '正在解析…',
  'import.unknownToken': '未知代币',
  'import.unnamedToken': '未命名代币',
  'import.decimals': '小数位数',
  'import.balance': '余额',
  'import.hint': '粘贴 {network} 上的代币合约地址并解析，即可在添加前预览代币。',
  'import.warning': '未验证的代币。添加前请自行核实合约地址。导入的代币不在精选白名单中，也没有价格数据。',
  'import.add': '添加代币',
  'import.error.cap': '此钱包导入的代币数量已达上限。请移除一个后再添加。',

  // NFT gallery and import-NFT sheet
  'nfts.empty.title': '暂无 NFT',
  'nfts.empty.body': '此钱包持有的 NFT 将显示在这里。可通过合约地址导入以进行追踪。',
  'nfts.import': '导入 NFT',
  'nfts.import.closeAria': '关闭导入 NFT',
  'nfts.import.aria': '将 NFT 导入 {wallet}',
  'nfts.import.title': '导入 NFT',
  'nfts.import.contract': '合约地址',
  'nfts.import.contractPlaceholder': '粘贴 NFT 合约地址',
  'nfts.import.objectId': '对象 ID',
  'nfts.import.objectIdPlaceholder': '粘贴 Sui 对象 ID',
  'nfts.import.tokenId': '代币 ID',
  'nfts.import.tokenIdPlaceholder': '合约内的代币 ID',
  'nfts.import.network': '网络',
  'nfts.import.submit': '导入',
  'nfts.import.submitting': '导入中…',
  'nfts.hidden.toggle': '已隐藏项目（{count}）',
  'nfts.hideAria': '隐藏 {name}',
  'nfts.unhideAria': '显示 {name}',
  'nfts.unsupported': '此网络暂不支持 NFT。',
  'nfts.error.load': '无法加载 NFT。',
  'nfts.loadMore': '加载更多',
  'nfts.itemFallback': 'NFT',

  // Wallet balance and wallet settings
  'walletBalance.tag.unverifiedTitle': '此代币不在精选白名单中。',
  'walletBalance.testnetPillTitle': '测试网钱包。余额没有实际价值。',
  'walletBalance.testnet': '测试网',
  'walletBalance.loadingBalances': '正在加载余额...',
  'walletBalance.statusPrefix': '状态：{status}',
  'walletBalance.keygen': '密钥正在生成。准备就绪后会显示余额。',
  'walletBalance.partial': '部分余额无法读取，数据可能不完整。',
  'walletBalance.chainFilterAria': '网络筛选器',
  'walletBalance.testnetBanner': '测试网 — 此处显示的是测试网余额，没有实际价值。要使用主网，请在菜单中关闭开发者模式。',
  'walletSettings.addressAria': '钱包地址',
  'walletSettings.detailsAria': '钱包详情',
  'walletSettings.address': '地址',
  'walletSettings.details': '详情',
  'walletSettings.addressPending': '地址待生成。钱包密钥生成完成后会显示。',
  'walletSettings.name': '名称',
  'walletSettings.balance': '余额',
  'walletSettings.status': '状态',
  'walletSettings.curve': '曲线',
  'walletSettings.publicKey': '公钥',
  'walletSettings.shortAddress': '短地址',
  'walletSettings.pending': '待处理',
  'walletSettings.unknownChain': '未知网络',

  // Welcome / language choice (first-visit screen)
  'welcome.eyebrow': '欢迎来到',
  'welcome.tagline': '守护珍视的一切',
  'welcome.title': '选择您的语言',
  'welcome.cta': '开始使用',
  'welcome.legalTitle': '法律声明',
  'welcome.legalText': '语言支持是我们为提升全球用户可访问性所作努力的一部分。该支持仅在法律允许此类使用的地区提供，且仅为偏好或需要使用相关语言的用户提供便利。',
  'welcome.legalAgreement': '点击下面的「同意」按钮，即表示您确认您使用此语言符合当地法律法规。',
  'welcome.agree': '同意',

  'start.label': '钱包',
  'start.points.badge': '由 MPC 技术驱动',
  'start.points.lead': '无需易泄露的私钥或助记词。',
  'start.points.shares':
    '您的钱包由分布在 Google、您的备份和设备身份验证中的安全要素（密钥分片）保护。',
  'start.points.twoRequired': '转移资产至少需要两个安全要素。',
  'start.points.neverWhole': '保护您资产的安全要素绝不会合并，也不会存储在同一处。',
  'start.points.rules': '即使某个安全要素出现问题，额外的安全规则仍会保护您的资产。',
  'start.points.more': '了解更多',
  'start.points.less': '收起',
  'start.title': '创建匿名钱包',
  'start.desc': '使用您设备上的身份验证功能保护您的去中心化钱包。',
  'start.hint': '人脸识别 · 指纹识别 · 通行密钥',
  'start.signin.prompt': '已有账户？',
  'start.signin.link': '登录',
  'start.unsupported': '此浏览器无法创建通行密钥。请改用第三方账户登录。',

  // Maintenance screen. Shown when the host answers /v1 with 503.
  'maintenance.title': '钱包维护中',
  'maintenance.body': '我们正在进行计划内维护。维护完成后，钱包将恢复使用。',
  'maintenance.assurance': '您的资金和密钥都安全无恙。退出登录不会改变您持有的任何资产。',
  'maintenance.retry': '重新检查',
  'start.secondAccount.title': '此设备上已注册与账户关联的验证方式',
  'start.secondAccount.body': '您要添加另一个账户吗？',
  'start.secondAccount.signIn': '前往登录页面',
  'start.secondAccount.create': '添加另一个账户',
  'send.form.fromAddress': '发送地址',
  'swap.signer.switchExplanation': '请选择兼容的钱包查看兑换报价。',
  'swap.signer.evmWallet': 'EVM 钱包',
  'swap.signer.chooseWallet': '选择钱包',
  'swap.confirm.slippage': '滑点容差',
  'swap.confirm.minimumReceived': '最低收到',
  'swap.approval.pending': '代币授权处理中',
  'swap.approval.pendingHint': '正在等待链上确认。',
  'swap.approval.confirmed': '代币授权已确认',
  'swap.approval.preparing': '正在使用刷新后的路线准备兑换。',
  'swap.approval.confirmedRetry': '授权已成功，兑换尚未发送。',
  'swap.approval.simulationFailed': '授权已确认，但刷新后的路线未通过安全模拟。兑换尚未发送。请刷新路线后重试。',
  'swap.transaction.passkey': '使用通行密钥批准交易。',
  'swap.settings.edit': '高级设置',
  'swap.settings.done': '完成',
  'swap.settings.custom': '自定义',
  'swap.settings.slippagePercent': '自定义滑点',
  'swap.settings.slippageError': '请输入 0.01% 至 5% 的滑点。',
  'swap.settings.gas': 'Gas 费',
  'swap.settings.automatic': '自动',
  'swap.settings.maxFee': '最高费用',
  'swap.settings.priorityFee': '优先费用',
  'swap.settings.gasHint': 'Gas 上限会根据本次兑换自动估算。',
  'swap.settings.gasError': '请输入有效费用。最高费用必须不低于优先费用。',
};
